/**
 * Centralised NZ-first date & time formatting helpers.
 * - All USER-FACING dates render as dd/MM/yyyy (NZ convention).
 * - All USER-FACING times render in Pacific/Auckland timezone.
 * - API/wire formats (yyyy-MM-dd, ISO) are NOT touched — this file only
 *   covers display formatting.
 */
import { format as fnsFormat, parseISO, isValid } from "date-fns";

const NZ_LOCALE = "en-NZ";
const NZ_TZ = "Pacific/Auckland";

const toDate = (input) => {
  if (!input && input !== 0) return null;
  if (input instanceof Date) return isValid(input) ? input : null;
  if (typeof input === "string") {
    const d = parseISO(input);
    if (isValid(d)) return d;
    const d2 = new Date(input);
    return isValid(d2) ? d2 : null;
  }
  const d = new Date(input);
  return isValid(d) ? d : null;
};

/** Format a date as `dd/MM/yyyy` (local date, no tz conversion). */
export const formatDate = (input, fallback = "—") => {
  const d = toDate(input);
  return d ? fnsFormat(d, "dd/MM/yyyy") : fallback;
};

/** Format a date-time as `dd/MM/yyyy HH:mm` in NZ time. */
export const formatDateTime = (input, fallback = "—") => {
  const d = toDate(input);
  if (!d) return fallback;
  return new Intl.DateTimeFormat(NZ_LOCALE, {
    timeZone: NZ_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
};

/** Format a time as `HH:mm` in NZ time. */
export const formatTime = (input, fallback = "—") => {
  const d = toDate(input);
  if (!d) return fallback;
  return new Intl.DateTimeFormat(NZ_LOCALE, {
    timeZone: NZ_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
};

/** Format a date as `dd/MM/yyyy HH:mm:ss` in NZ time (for logs / audit). */
export const formatDateTimeSec = (input, fallback = "—") => {
  const d = toDate(input);
  if (!d) return fallback;
  return new Intl.DateTimeFormat(NZ_LOCALE, {
    timeZone: NZ_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
};

/** Format a date as `MMM yyyy` for billing cycles (locale-independent abbreviation). */
export const formatMonthYear = (input, fallback = "—") => {
  const d = toDate(input);
  return d ? fnsFormat(d, "MMM yyyy") : fallback;
};

/** Format a date range as `dd/MM/yyyy - dd/MM/yyyy`. */
export const formatDateRange = (start, end, fallback = "—") => {
  const s = formatDate(start, null);
  const e = formatDate(end, null);
  if (!s && !e) return fallback;
  return `${s || fallback} - ${e || fallback}`;
};
